import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Menu from '../../element/ui/core/menu';
import AnnotationOptionPanelHeader from '../common/AnnotationOptions/AnnotationOptionPanelHeader';
import SidebarPanelButton from '../common/SidebarPanelButton';
import { useStore } from '../../hooks';
import { ADJUST_TAB_TOOLS, TOOLS_ITEMS } from '../tools/tools.constants';
import FlipX from '../tools/Flip/FlipX';
import FlipY from '../tools/Flip/FlipY';
import {
  ANNOTATION_OPTION_POPUP_MIN_WIDTH,
  StyledOptionPopupContent,
  StyledOptions,
  StyledSpacedOptionFields,
} from '../common/AnnotationOptions/AnnotationOptions.styled';
import { TABS_IDS, TOOLS_IDS } from '../../utils/constants';
import {
  canResetAdjustTool,
  getAdjustToolResetActions,
  isAdjustToolApplied,
} from '../../utils/adjustToolState';
import {
  ADJUST_FLIP_TOOL_IDS,
  ADJUST_POPUP_TOOL_META,
} from './adjustTabTools.config';

const ADJUST_TAB_IDS = new Set([TABS_IDS.ADJUST, TABS_IDS.FINETUNE]);

const AdjustTabOptions = ({ tabTools, toolId, selectTool, t, useCloudimage }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [popupToolId, setPopupToolId] = useState(null);
  const { dispatch, adjustments, finetunesProps, resize } = useStore();

  const visibleTools = useMemo(
    () =>
      (tabTools.length ? tabTools : ADJUST_TAB_TOOLS).filter((id) => {
        if (ADJUST_FLIP_TOOL_IDS.has(id)) {
          return Boolean(TOOLS_ITEMS[id]?.Item);
        }
        return Boolean(ADJUST_POPUP_TOOL_META[id] && TOOLS_ITEMS[id]?.ItemOptions);
      }),
    [tabTools],
  );

  const openPopup = useCallback(
    (e, newToolId) => {
      selectTool(newToolId);
      setAnchorEl(e?.currentTarget ?? null);
      setPopupToolId(newToolId);
    },
    [selectTool],
  );

  const closePopup = useCallback(() => {
    setAnchorEl(null);
    setPopupToolId(null);
  }, []);

  const PopupComponent =
    popupToolId && TOOLS_ITEMS[popupToolId]?.ItemOptions;

  const popupMeta = popupToolId && ADJUST_POPUP_TOOL_META[popupToolId];

  const adjustState = { adjustments, finetunesProps };

  const resetPopupTool = useCallback(() => {
    if (!popupToolId) {
      return;
    }
    getAdjustToolResetActions(popupToolId, { resize }).forEach((action) => {
      dispatch(action);
    });
  }, [dispatch, popupToolId, resize]);

  return (
    <StyledOptions className="respark_adjust-tab-options respark_annotations-options">
      <div className="respark_annotation-options-editable">
        {visibleTools.map((id) => {
          if (ADJUST_FLIP_TOOL_IDS.has(id)) {
            const FlipItem = id === TOOLS_IDS.FLIP_X ? FlipX : FlipY;
            return (
              <div key={id} className="respark_adjust-tab-flip-action">
                <FlipItem
                  selectTool={selectTool}
                  t={t}
                  isSelected={toolId === id}
                />
              </div>
            );
          }

          const meta = ADJUST_POPUP_TOOL_META[id];
          if (!meta) {
            return null;
          }

          const { titleKey, Icon } = meta;
          const label = t(titleKey);

          return (
            <SidebarPanelButton
              key={id}
              className="respark_annotation-option-triggerer respark_adjust-tab-action"
              Icon={Icon}
              label={label}
              title={label}
              aria-selected={
                isAdjustToolApplied(id, adjustState) ||
                (toolId === id && Boolean(anchorEl))
              }
              onClick={(e) => openPopup(e, id)}
            />
          );
        })}

        {PopupComponent && (
          <Menu
            className="respark_annotation-option-popup respark_adjust-tab-popup"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closePopup}
            position="top"
            containerProps={{
              style: {
                minWidth: ANNOTATION_OPTION_POPUP_MIN_WIDTH,
                maxHeight: 'none',
                overflow: 'visible',
                overflowY: 'visible',
                overflowX: 'visible',
              },
            }}
          >
            <StyledOptionPopupContent className="respark_annotation-option-popup-content">
              {popupMeta ? (
                <AnnotationOptionPanelHeader
                  title={t(popupMeta.titleKey)}
                  closeLabel={t('annotationOptionClose')}
                  resetLabel={t('reset')}
                  canReset={
                    canResetAdjustTool(popupToolId) &&
                    isAdjustToolApplied(popupToolId, adjustState)
                  }
                  onClose={closePopup}
                  onReset={resetPopupTool}
                />
              ) : null}
              <StyledSpacedOptionFields
                className={`respark_annotation-option-panel respark_adjust-tool-option-panel${
                  popupToolId === TOOLS_IDS.HSV ? ' respark_hsv-option-panel' : ''
                }`}
              >
                <PopupComponent t={t} />
              </StyledSpacedOptionFields>
            </StyledOptionPopupContent>
          </Menu>
        )}
      </div>
    </StyledOptions>
  );
};

AdjustTabOptions.propTypes = {
  tabTools: PropTypes.arrayOf(PropTypes.string).isRequired,
  toolId: PropTypes.string,
  selectTool: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  useCloudimage: PropTypes.bool,
};

export { ADJUST_TAB_IDS };
export default AdjustTabOptions;
